'use client';

import { useEffect, useState } from 'react';
import type { TocHeading } from '@/lib/toc';

// Header fisso (h-20) + margine: deve combaciare con scroll-mt-28 sui titoli
const SCROLL_OFFSET = 120;

export default function TableOfContents({ headings }: { headings: TocHeading[] }) {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const elements = headings
      .map(function(h) { return document.getElementById(h.id); })
      .filter(function(el): el is HTMLElement { return el !== null; });
    if (elements.length === 0) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      let current: string | null = null;
      for (const el of elements) {
        if (el.getBoundingClientRect().top - SCROLL_OFFSET <= 1) current = el.id;
        else break;
      }
      setActiveId(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [headings]);

  const list = (
    <ol className="space-y-1 text-sm">
      {headings.map(function(h) {
        const active = h.id === activeId;
        return (
          <li key={h.id} className={h.level === 3 ? 'pl-4' : ''}>
            <a
              href={'#' + h.id}
              aria-current={active ? 'location' : undefined}
              className={
                'block border-l-2 py-1 pl-3 leading-snug transition-colors duration-200 ' +
                (active
                  ? 'border-primary-500 text-primary-600 font-medium'
                  : 'border-transparent text-secondary-400 hover:text-secondary-500 hover:border-secondary-200')
              }
            >
              {h.text}
            </a>
          </li>
        );
      })}
    </ol>
  );

  return (
    <>
      {/* Mobile / tablet: indice richiudibile sopra l'articolo */}
      <details className="lg:hidden mb-10 rounded-xl border border-secondary-100 bg-secondary-50/50 px-5 py-4 group">
        <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-semibold text-secondary-500">
          In questo articolo
          <svg className="w-4 h-4 text-secondary-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </summary>
        <nav aria-label="Indice dell'articolo" className="mt-4">
          {list}
        </nav>
      </details>

      {/* Desktop: indice sticky nella colonna laterale */}
      <nav aria-label="Indice dell'articolo" className="hidden lg:block sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto">
        <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-secondary-400">
          In questo articolo
        </p>
        {list}
      </nav>
    </>
  );
}
